import { Card } from "@/src/components/cards";
import {
  closeDialog,
  closeDrawer,
  openDialog,
  openDrawer,
} from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import { IconTab } from "@/src/components/iconTab";
import StaggeredAnimation from "@/src/components/staggeredAnimationComponent";
import { Input } from "@/src/components/universalInput";
import {
  Bed,
  Hospitalisation,
  PatientItemStatus,
  Service,
  TransferDemand,
  patientItemStatuses,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  getHospitalId,
  getItemId,
  getName,
  getServiceId,
  getUserShortName,
  jsonAbstract,
  openToast,
  uid,
} from "@/src/utils/utils";
import moment from "moment";
import React, { useState } from "react";
import { openTargetUsersDrawer } from "../admin/users/usersHook";
import { sendSimpleNotification } from "../notifications/notificationsHook";
import { HospitalisationBedsChoice } from "./hospitalisationHook";
import { ServicesChoice } from "../hospitalisations/servicesChoice";
import { Button } from "@/src/components/buttons";
import Barcode from "react-barcode";

export function TransferDemandsList({
  service,
  onTransferSuccess,
}: {
  service: Service | null | undefined;
  onTransferSuccess: (transfer: TransferDemand) => void;
}) {
  const [reload, setReload] = useState(0);
  const [selectedStatus, setSelectedStatus] =
    useState<PatientItemStatus>("pending");

  let availableStatuses: PatientItemStatus[] = [
    "pending",
    "validated",
    "refused",
  ];

  return (
    <div className="c1 gap15">
      <div className="c3 gap15">
        {availableStatuses.map((status) => {
          let statusInfo = patientItemStatuses[status];
          return (
            <IconTab
              key={status}
              activeTabId={selectedStatus}
              id={status}
              name={statusInfo?.name}
              onChange={(newId) => {
                setSelectedStatus(newId as PatientItemStatus);
              }}
            ></IconTab>
          );
        })}
      </div>
      <FetchableList
        queryKey={["TransferDemandsList", service?.id, selectedStatus, reload]}
        queryFn={() =>
          apiFetch("transferDemands", "loadMany", {
            serviceId: getItemId(service),
            statuses: [selectedStatus],
          })
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((transfer, index) => {
              const patient = transfer?.patient;
              return (
                <StaggeredAnimation key={uid()} index={index}>
                  <Card
                    className="empty"
                    title={
                      <div className="center">
                        <Barcode
                          value={
                            patient?.nin ||
                            patient?.id
                              ?.replace("patients:", "")
                              ?.toString()
                              ?.slice(0, 8) ||
                            ""
                          }
                          displayValue={false}
                          height={30}
                          width={1}
                          fontSize={12}
                          margin={0}
                        ></Barcode>
                        <div className="black   ">NIS</div>
                        <div className=" bold blueish">{patient?.nin}</div>
                      </div>
                    }
                    color="blue"
                    onClick={async () => {
                      await openDrawer({
                        id: "bedDrawer",
                        title: "Lit",
                        content: (
                          <HospitalisationBedsChoice
                            service={USER.get()?.service!}
                            onSelect={async (newBed) => {
                              let hospitalisationId = getItemId(
                                transfer?.parent
                              );
                              if (!hospitalisationId)
                                throw new Error(
                                  "hospitalisationId is not defined"
                                );

                              await apiFetch("hospitalisations", "addOne", {
                                hospitalisation: {
                                  id: hospitalisationId,
                                  bed: newBed,
                                },
                              });

                              transfer.status = "validated";
                              apiFetch("transferDemands", "addOne", {
                                transfer,
                              });

                              sendSimpleNotification({
                                type: "transferAnswer",
                                item: transfer,
                                targetUsers: [getItemId(transfer.creator)],
                              });
                              setReload((prev) => prev + 1);
                              onTransferSuccess(transfer);
                              closeDrawer("bedDrawer");
                            }}
                          />
                        ),
                      });
                    }}
                    onDelete={async () => {
                      transfer.status = "refused";
                      let result = await apiFetch("transferDemands", "addOne", {
                        transfer,
                      });
                      if (!result) return;
                      setReload((prev) => prev + 1);
                    }}
                  >
                    <div className="bold blue-color mtop15">
                      <div className="capitalize">
                        {getName(transfer?.patient)}
                      </div>
                    </div>

                    <div className="gray">
                      {getUserShortName(transfer?.creator) || ""}
                    </div>

                    <div className="gray">
                      {moment(transfer._date).format("LL HH:mm")}{" "}
                    </div>
                  </Card>
                </StaggeredAnimation>
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

export async function openTransferDrawer(
  hospitalisation: Hospitalisation,
  transfer: TransferDemand,
  onConfirm: (transfer: TransferDemand) => void
) {
  async function onServiceSelect(service: Service) {
    transfer.service = service;
    if (getItemId(transfer.service) === getItemId(USER.get()?.service)) {
      await openDrawer({
        id: "bedDrawer",
        title: "Lit",
        content: (
          <HospitalisationBedsChoice
            service={USER.get()?.service!}
            enableAll={true}
            onSelect={async (newBed, existingHospitalisations) => {
              let oldBed = jsonAbstract(hospitalisation.bed) as Bed | undefined;

              hospitalisation.bed = newBed;
              await apiFetch("hospitalisations", "addOne", {
                hospitalisation: {
                  id: getItemId(hospitalisation),
                  status: "inProgress",
                  bed: newBed,
                },
              });
              transfer.status = "finished";
              transfer.bed = newBed;

              let demand = await apiFetch("transferDemands", "addOne", {
                transfer: transfer,
              });

              if (existingHospitalisations.length > 0) {
                for (let existingHospitalisation of existingHospitalisations) {
                  existingHospitalisation.bed = oldBed;
                  await apiFetch("hospitalisations", "addOne", {
                    hospitalisation: {
                      id: getItemId(existingHospitalisation),
                      bed: oldBed,
                    },
                  });
                }
              }

              onConfirm?.(demand);

              closeDrawer("bedDrawer");
              closeDrawer("serviceChoiceDrawer");
              closeDrawer("sortieTypeDrawer");
              openToast("success", "Transfer effectué avec succès");
            }}
          />
        ),
      });

      return true;
    }

    await openDrawer({
      id: "transferDrawer",
      title: "Demande de transfert",
      additionalClasses: ["small"],

      content: (
        <TransferForm
          transfer={transfer}
          onChange={(newValue) => {
            transfer = newValue;
          }}
        ></TransferForm>
      ),
      onConfirm: async () => {
        await openTargetUsersDrawer({
          targetUsers: [],
          serviceId: getItemId(transfer.service),
          onConfirm: async (targetUsers) => {
            let result = await apiFetch(
              "transferDemands",
              "addOne",
              {
                transfer,
              },
              "Erreur lors de l'enregistrement de la demande de transfert"
            );
            if (!result) return;
            onConfirm?.(result);
            closeDrawer("serviceChoiceDrawer");
            closeDrawer("sortieTypeDrawer");
            openToast("success", "Demande de transfer envoyée avec succès");
            await sendSimpleNotification({
              item: result,
              type: "transferDemand",
              serviceId: transfer.service?.id,
              targetUsers: jsonAbstract(targetUsers.map((u) => u.id)),
            });
          },
        });
      },
    });
  }

  await openDialog({
    id: "transferTypeDialog",
    title: "Tranfert",
    content: (
      <div className="c1 gap10">
        <Button
          type="tertiary"
          onClick={async () => {
            onServiceSelect(USER.get()?.service!);
            closeDialog("transferTypeDialog");
          }}
        >
          Vers un autre lit
        </Button>
        <Button
          type="tertiary"
          onClick={async () => {
            closeDialog("transferTypeDialog");
            await openDrawer({
              id: "serviceChoiceDrawer",
              title: "Choisir un service",
              additionalClasses: ["small"],
              content: (
                <ServicesChoice
                  types={["hospitalisations"]}
                  hospitalId={getHospitalId()}
                  current={transfer.service}
                  onChange={async (service) => {
                    transfer.service = service;
                    await onServiceSelect(service);
                  }}
                ></ServicesChoice>
              ),
            });
          }}
        >
          Vers un autre service
        </Button>
      </div>
    ),
  });
}

export function TransferForm({
  transfer,
  onChange,
}: {
  transfer: TransferDemand;
  onChange?: (newValue: TransferDemand) => void;
}) {
  const [current, setCurrent] = React.useState(transfer);
  function onLocalChange(newValue: TransferDemand) {
    setCurrent(newValue);
    onChange?.(newValue);
  }
  return (
    <div className="c1 gap10">
      <Input
        type="textarea"
        name={"Notes"}
        value={current?.notes}
        onChange={(newValue) => {
          onLocalChange({ ...current, notes: newValue });
        }}
      ></Input>
    </div>
  );
}
